import React, { useState, useEffect, useRef } from 'react';
import { Container, Row, Col, Button, Form, Offcanvas, Badge, Pagination, Collapse } from 'react-bootstrap';
import { BsFilter, BsSearch, BsPhone, BsCollection, BsCurrencyDollar, BsX, BsSortDown, BsChevronDown, BsChevronRight, BsArrowUp } from 'react-icons/bs';
import { useLocation, useSearchParams, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import ProductCard from './ProductCard';
import { useProducts } from '../../context/ProductContext';
import { useCollections } from '../../context/CollectionContext';
import { useCart } from '../../context/CartContext';
import { useProductTracking } from '../../hooks/useProductTracking';
import './Products.css';
import { toast } from 'react-toastify';
import CustomDesignCard from './CustomDesignCard';
import ShoppingCartSidebar from '../cart/ShoppingCartSidebar';

const defaultImage = "https://via.placeholder.com/300x300?text=No+Image";

// Sayfa başına kaydırma yardımcı fonksiyonu
const scrollToPageTop = () => {
  try {
    // Tüm tarayıcılar için çalışan yöntemler
    if (typeof window !== 'undefined') {
      // Animasyonsuz direkt scroll - daha güvenilir
      window.scrollTo(0, 0);
      
      // Alternatif yöntem
      window.scrollTo({
        top: 0,
        left: 0,
        behavior: 'auto' // 'smooth' yerine 'auto' kullanarak anında kaydırma
      });
    }
    
    // Safari ve iOS için
    if (typeof document !== 'undefined') {
      // Standart yöntemler
      document.body.scrollTop = 0;
      document.documentElement.scrollTop = 0;
      
      // scrollingElement yöntemi (modern tarayıcılar için)
      if (document.scrollingElement) {
        document.scrollingElement.scrollTop = 0;
      }
      
      // iOS için ek önlem
      if (/iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream) {
        // iOS'ta sayfanın en üstüne kaydırmak için ek yöntem
        setTimeout(() => {
          window.scrollTo(0, 0);
          document.body.scrollTop = 0;
          document.documentElement.scrollTop = 0;
          if (document.scrollingElement) {
            document.scrollingElement.scrollTop = 0;
          }
        }, 50);
      }
    }
  } catch (error) {
    console.error('Scroll error:', error);
  }
};

// Mobile view component
const MobileProductsView = ({
  collections,
  selectedCollections,
  setSelectedCollections,
  priceRange,
  setPriceRange,
  phoneModel,
  setPhoneModel,
  phoneModels,
  selectedPhoneModels,
  setSelectedPhoneModels,
  handleCollectionChange,
  handlePriceChange,
  handlePhoneModelChange,
  handlePhoneModelCheckboxChange,
  handleSort,
  displayLimit,
  totalProducts,
  onLoadMore,
  searchText,
  setSearchText,
  activeFilters,
  sortType,
  setSortType,
  pagination,
  loading,
  currentPage,
  searchParams,
  navigate,
  mobileIsSticky,
  mobileHeaderRef
}) => {
  const [showFilters, setShowFilters] = useState(false);
  const [showCollections, setShowCollections] = useState(false);
  const [showPhoneModels, setShowPhoneModels] = useState(false);

  // Aktif filtre sayısını hesapla
  const filterCount = activeFilters.length;

  // Filtre butonuna tıklandığında
  const handleFilterClick = () => {
    setShowFilters(true);
    // Body scroll'unu engelle
    document.body.style.overflow = 'hidden';
    if (window.handleFilterScroll) {
      window.handleFilterScroll();
    }
  };

  const handleFilterClose = () => {
    setShowFilters(false);
    // Body scroll'unu geri aç
    document.body.style.overflow = 'auto';
  };

  // Offcanvas show state'ini izle
  useEffect(() => {
    if (!showFilters) {
      // Offcanvas kapandığında body scroll'unu geri aç
      document.body.style.overflow = 'auto';
    }
  }, [showFilters]);

  return (
    <div className="mobile-products-view d-lg-none">
      <div 
        className={`mobile-header ${mobileIsSticky ? 'sticky-active' : ''}`}
        ref={mobileHeaderRef}
        style={mobileIsSticky ? {
          position: 'fixed',
          top: '60px',
          left: 0,
          right: 0,
          zIndex: 1000,
          backgroundColor: 'white',
          willChange: 'transform',
          transform: 'translateZ(0)'
        } : {}}
      >
        <Container fluid>
          <Row className="align-items-center">
            <Col>
              <div className="search-wrapper">
                <BsSearch className="search-icon" />
                <Form.Control
                  type="text"
                  placeholder="Ürün ara..."
                  value={searchText}
                  onChange={(e) => setSearchText(e.target.value)}
                  className="search-input"
                />
              </div>
            </Col>
            <Col xs="auto">
              <Button 
                variant="outline-primary" 
                onClick={handleFilterClick}
                className="filter-button"
              >
                <BsFilter className="me-1" />
                Filtrele
                {filterCount > 0 && (
                  <Badge bg="primary" pill className="ms-2">
                    {filterCount}
                  </Badge>
                )}
              </Button>
            </Col>
          </Row>
        </Container>
      </div>

      <Offcanvas show={showFilters} onHide={handleFilterClose} placement="bottom" className="products-offcanvas mobile-filter-offcanvas">
        <Offcanvas.Header className="mobile-filter-header">
          <div className="d-flex justify-content-between align-items-center w-100">
            <div className="d-flex align-items-center">
              <BsFilter className="me-2" />
              <Offcanvas.Title className="mb-0">Filtreler</Offcanvas.Title>
              {filterCount > 0 && (
                <Badge bg="primary" pill className="ms-2">
                  {filterCount}
                </Badge>
              )}
            </div>
            <div className="d-flex align-items-center gap-2">
              {activeFilters.length > 0 && (
                <Button 
                  variant="outline-danger" 
                  size="sm"
                  onClick={() => {
                    setSearchText('');
                    setSelectedCollections([]);
                    setPhoneModel('');
                    setSelectedPhoneModels([]);
                    setPriceRange([0, 1000]);
                  }}
                >
                  Temizle
                </Button>
              )}
              <Button 
                variant="outline-secondary" 
                size="sm"
                onClick={handleFilterClose}
                className="close-btn"
              >
                <BsX size={20} />
              </Button>
            </div>
          </div>
        </Offcanvas.Header>
        <Offcanvas.Body className="mobile-filter-body">
          <div className="mobile-filters">
            {activeFilters.length > 0 && (
              <div className="active-filters mb-3">
                <h6 className="mb-2 text-muted">Aktif Filtreler</h6>
                <div className="filter-badges">
                  {activeFilters.map((filter, index) => {
                    const handleFilterRemove = (e) => {
                      e.stopPropagation();
                      // İlgili filtreyi kaldır
                      if (filter.includes('Koleksiyon:')) {
                        handleCollectionChange(filter.split(': ')[1]);
                      } else if (filter.includes('Model:')) {
                        const modelName = filter.split(': ')[1];
                        const model = phoneModels.find(m => `${m.brand} ${m.model_name}` === modelName);
                        if (model) {
                          // Hem phoneModel hem de selectedPhoneModels'den kaldır
                          if (phoneModel === model.id.toString()) {
                            setPhoneModel('');
                          }
                          // selectedPhoneModels'den kaldır
                          setSelectedPhoneModels(prev => prev.filter(id => id !== model.id));
                          // URL'den phone_model parametresini kaldır
                          const newSearchParams = new URLSearchParams(searchParams);
                          newSearchParams.delete('phone_model');
                          navigate(`/products?${newSearchParams.toString()}`, { replace: true });
                        }
                      } else if (filter.includes('Arama:')) {
                        // Arama filtresini kaldır
                        setSearchText('');
                        // URL'den search parametresini kaldır
                        const newSearchParams = new URLSearchParams(searchParams);
                        newSearchParams.delete('search');
                        navigate(`/products?${newSearchParams.toString()}`, { replace: true });
                      } else if (filter.includes('Fiyat:')) {
                        // Fiyat aralığı filtresini varsayılan değerlere sıfırla
                        setPriceRange([0, 1000]);
                        // URL'den fiyat parametrelerini kaldır
                        const newSearchParams = new URLSearchParams(searchParams);
                        newSearchParams.delete('min_price');
                        newSearchParams.delete('max_price');
                        navigate(`/products?${newSearchParams.toString()}`, { replace: true });
                      }
                    };

                    return (
                      <Badge 
                        key={index} 
                        bg="light" 
                        text="dark" 
                        className="me-2 mb-2 filter-badge d-inline-flex align-items-center"
                        style={{ cursor: 'pointer' }}
                        onClick={handleFilterRemove}
                      >
                        <span>{filter}</span>
                        <BsX 
                          className="ms-1" 
                          style={{ cursor: 'pointer' }}
                          onClick={handleFilterRemove}
                        />
                      </Badge>
                    );
                  })}
                </div>
              </div>
            )}

            <div className="filter-section">
              <div 
                className="filter-title-clickable"
                onClick={() => setShowCollections(!showCollections)}
                style={{ cursor: 'pointer' }}
              >
                <h6 className="filter-title d-flex justify-content-between align-items-center">
                  <span>
                    <BsCollection className="me-2" />
                    Koleksiyonlar
                  </span>
                  {showCollections ? (
                    <BsChevronDown />
                  ) : (
                    <BsChevronRight />
                  )}
                </h6>
              </div>
              <Collapse in={showCollections}>
                <div>
                  {collections.map(collection => (
                    <Form.Check
                      key={collection}
                      type="checkbox"
                      label={collection}
                      checked={selectedCollections.includes(collection)}
                      onChange={() => handleCollectionChange(collection)}
                      className="mb-2"
                    />
                  ))}
                </div>
              </Collapse>
            </div>

            <div className="filter-section">
              <div 
                className="filter-title-clickable"
                onClick={() => setShowPhoneModels(!showPhoneModels)}
                style={{ cursor: 'pointer' }}
              >
                <h6 className="filter-title d-flex justify-content-between align-items-center">
                  <span>
                    <BsPhone className="me-2" />
                    Telefon Modeli
                  </span>
                  {showPhoneModels ? (
                    <BsChevronDown />
                  ) : (
                    <BsChevronRight />
                  )}
                </h6>
              </div>
              <Collapse in={showPhoneModels}>
                <div>
                  {phoneModels.filter(model => model.is_active).map(model => (
                    <Form.Check
                      key={model.id}
                      type="checkbox"
                      label={`${model.brand} ${model.model_name}`}
                      checked={selectedPhoneModels.includes(model.id)}
                      onChange={() => handlePhoneModelCheckboxChange(model.id)}
                      className="mb-2"
                    />
                  ))}
                </div>
              </Collapse>
            </div>

            <div className="filter-section">
              <h6 className="filter-title">
                <BsCurrencyDollar className="me-2" />
                Fiyat Aralığı
              </h6>
              <Row className="g-2">
                <Col>
                  <Form.Control
                    type="number"
                    value={priceRange[0]}
                    onChange={(e) => handlePriceChange([parseInt(e.target.value), priceRange[1]])}
                    min="0"
                    placeholder="Min"
                    className="custom-input"
                  />
                </Col>
                <Col xs="auto" className="d-flex align-items-center">-</Col>
                <Col>
                  <Form.Control
                    type="number"
                    value={priceRange[1]}
                    onChange={(e) => handlePriceChange([priceRange[0], parseInt(e.target.value)])}
                    min="0"
                    placeholder="Max"
                    className="custom-input"
                  />
                </Col>
              </Row>
            </div>

            <div className="filter-section">
              <h6 className="filter-title">
                <BsSortDown className="me-2" />
                Sıralama
              </h6>
              <Form.Select 
                value={sortType}
                onChange={(e) => setSortType(e.target.value)}
                className="custom-select"
              >
                <option value="default">Öne Çıkanlar Önce</option>
                <option value="price-asc">Fiyat: Düşükten Yükseğe</option>
                <option value="price-desc">Fiyat: Yüksekten Düşüğe</option>
                <option value="newest">En Yeniler</option>
              </Form.Select>
            </div>

            {/* Apply Filters Button - Mobile */}
            <div className="mobile-filter-actions mt-4">
              <Row className="g-2">
                <Col>
                  <Button 
                    variant="outline-secondary" 
                    className="w-100"
                    onClick={handleFilterClose}
                  >
                    İptal
                  </Button>
                </Col>
                <Col>
                  <Button 
                    variant="primary" 
                    className="w-100"
                    onClick={handleFilterClose}
                  >
                    Uygula
                  </Button>
                </Col>
              </Row>
            </div>
          </div>
        </Offcanvas.Body>
      </Offcanvas>
    </div>
  );
};

function Products() {
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { 
    products, 
    phoneModels,
    loading: productsLoading, 
    error: productsError, 
    pagination,
    fetchProducts,
    fetchPhoneModels 
  } = useProducts();
  const { collections, collectionsWithCounts, loading: collectionsLoading, loadingCounts, error: collectionsError, fetchCollections, fetchCollectionsWithCounts } = useCollections();
  const { cart, updateQuantity, removeFromCart } = useCart();
  const { trackProductView, trackProductClick, trackProductRemoveFromCart } = useProductTracking();
  
  const [selectedCollections, setSelectedCollections] = useState([]);
  const [priceRange, setPriceRange] = useState([0, 1000]);
  const [phoneModel, setPhoneModel] = useState('');
  const [selectedPhoneModels, setSelectedPhoneModels] = useState([]);
  const [imageErrors, setImageErrors] = useState({});
  const [searchText, setSearchText] = useState('');
  const [sortType, setSortType] = useState('default');
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [showDesktopCollections, setShowDesktopCollections] = useState(false);
  const [showDesktopPhoneModels, setShowDesktopPhoneModels] = useState(false);
  
  const isInitialFetch = useRef(true);
  const navigate = useNavigate();
  
  // Sticky için ref'ler ve state
  const filtersWrapperRef = useRef(null);
  const mobileHeaderRef = useRef(null);
  const filtersColumnRef = useRef(null);
  const productsMainRowRef = useRef(null);
  const [isSticky, setIsSticky] = useState(false);
  const [mobileIsSticky, setMobileIsSticky] = useState(false);
  const [filtersWrapperWidth, setFiltersWrapperWidth] = useState(null);
  const [showScrollTop, setShowScrollTop] = useState(false);

  // URL'den koleksiyon parametresini kontrol et ve seç
  useEffect(() => {
    const collectionFromUrl = searchParams.get('collection');
    if (collectionFromUrl && collections.some(c => c.name === collectionFromUrl)) {
      setSelectedCollections([collectionFromUrl]);
    }
  }, [searchParams, collections]);

  // URL'den telefon modeli parametresini kontrol et ve seç
  useEffect(() => {
    const phoneModelFromUrl = searchParams.get('phone_model');
    if (phoneModelFromUrl && phoneModels.length > 0) {
      const modelId = parseInt(phoneModelFromUrl);
      const model = phoneModels.find(m => m.id === modelId && m.is_active);
      if (model) {
        setPhoneModel(modelId.toString());
        // Desktop checkbox'ları için selectedPhoneModels'e de ekle
        setSelectedPhoneModels(prev => {
          if (!prev.includes(modelId)) {
            return [...prev, modelId];
          }
          return prev;
        });
        // Telefon modeli filtresini aç
        setShowDesktopPhoneModels(true);
      }
    } else if (!phoneModelFromUrl) {
      // URL'de phone_model yoksa seçimleri temizle
      setPhoneModel('');
      setSelectedPhoneModels([]);
    }
  }, [searchParams, phoneModels]);

  // İlk yükleme ve filtre değişiklikleri için tek bir useEffect
  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);

        // İlk yüklemede koleksiyonlar ve telefon modellerini getir
        if (isInitialFetch.current) {
          // Önce koleksiyonları hızlıca yükle
          await fetchCollections();
          // Sonra ürün sayılarını ayrı olarak getir
          fetchCollectionsWithCounts();
          // Telefon modellerini de getir
          await fetchPhoneModels();
          isInitialFetch.current = false;
        }

        // Query parametrelerini oluştur
        const params = {};

        // Koleksiyon filtresi
        if (selectedCollections.length > 0) {
          params.collections = selectedCollections.join(',');
        }

        // Telefon modeli filtresi
        if (phoneModel) {
          params.phone_model = phoneModel;
        }

        // Fiyat filtresi
        if (priceRange[0] > 0) {
          params.min_price = priceRange[0];
        }
        if (priceRange[1] < 1000) {
          params.max_price = priceRange[1];
        }

        // Arama filtresi
        if (searchText) {
          params.search = searchText;
        }

        // Sıralama
        if (sortType !== 'default') {
          params.sort = sortType;
        }

        // Sayfalama
        params.page = 1;
        params.limit = 12;

        // Filtre değiştiğinde sayfa numarasını sıfırla
        setCurrentPage(1);

        // Ürünleri getir
        await fetchProducts(params);
      } catch (error) {
        console.error('Error loading data:', error);
        toast.error('Veriler yüklenirken bir hata oluştu. Lütfen sayfayı yenileyin.');
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [selectedCollections, phoneModel, priceRange, searchText, sortType, selectedPhoneModels]);

  // İlk render'da width'i al ve resize'da güncelle
  useEffect(() => {
    const updateWidth = () => {
      if (filtersWrapperRef.current) {
        const width = filtersWrapperRef.current.offsetWidth;
        setFiltersWrapperWidth(width);
      }
    };

    // İlk yüklemede
    updateWidth();

    // Resize event listener
    window.addEventListener('resize', updateWidth, { passive: true });

    return () => {
      window.removeEventListener('resize', updateWidth);
    };
  }, []);

  // Sticky scroll handler
  useEffect(() => {
    const handleScroll = () => {
      // Desktop filters için
      if (filtersWrapperRef.current && filtersColumnRef.current && productsMainRowRef.current) {
        const filtersRect = filtersWrapperRef.current.getBoundingClientRect();
        const columnRect = filtersColumnRef.current.getBoundingClientRect();
        const rowRect = productsMainRowRef.current.getBoundingClientRect();
        
        // Filtrelerin üst pozisyonu navbar'ın altına geldi mi?
        const shouldBeSticky = filtersRect.top <= 96; // Desktop navbar (76px) + 20px margin
        
        // Parent container'ın (products-main-row) alt sınırı
        // Filtrelerin parent container'ı footer'a gelmeden önce sticky'den çık
        const rowBottom = rowRect.bottom;
        const viewportHeight = window.innerHeight;
        const stickyTop = 96; // Sticky pozisyonu
        const filtersHeight = filtersRect.height;
        
        // Filtrelerin alt sınırı parent container'ın alt sınırından geçmemeli
        // Yani: sticky pozisyonu + filtrelerin yüksekliği <= parent container'ın alt sınırı
        const maxStickyBottom = stickyTop + filtersHeight;
        const canStaySticky = maxStickyBottom <= rowBottom;
        
        // Sticky olmalı mı? (üst sınır geçildi VE parent container'ın alt sınırı geçilmedi)
        const shouldStaySticky = shouldBeSticky && canStaySticky;
        
        if (shouldStaySticky && !isSticky) {
          setIsSticky(true);
        } else if (!shouldStaySticky && isSticky) {
          setIsSticky(false);
        }
      }

      // Mobile header için
      if (mobileHeaderRef.current) {
        const rect = mobileHeaderRef.current.getBoundingClientRect();
        const shouldBeSticky = rect.top <= 60; // Mobil navbar yüksekliği
        
        if (shouldBeSticky && !mobileIsSticky) {
          setMobileIsSticky(true);
        } else if (!shouldBeSticky && mobileIsSticky) {
          setMobileIsSticky(false);
        }
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    
    // İlk yüklemede kontrol et
    handleScroll();

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [isSticky, mobileIsSticky]);

  // Scroll to top butonu için scroll takibi
  useEffect(() => {
    const handleScrollTop = () => {
      // 300px'den fazla scroll edildiyse butonu göster
      const scrollY = window.scrollY || window.pageYOffset;
      setShowScrollTop(scrollY > 300);
    };

    window.addEventListener('scroll', handleScrollTop, { passive: true });
    
    // İlk yüklemede kontrol et
    handleScrollTop();

    return () => {
      window.removeEventListener('scroll', handleScrollTop);
    };
  }, []);

  // Yukarı çıkma fonksiyonu
  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };

  const handleCollectionChange = (collection) => {
    setSelectedCollections(prev => 
      prev.includes(collection) 
        ? prev.filter(c => c !== collection)
        : [...prev, collection]
    );
  };

  const handlePriceChange = (newRange) => {
    setPriceRange(newRange);
  };

  const handlePhoneModelChange = (value) => {
    setPhoneModel(value);
  };

  const handlePhoneModelCheckboxChange = (modelId) => {
    setSelectedPhoneModels(prev => {
      const isSelected = prev.includes(modelId);
      const newSelected = isSelected
        ? prev.filter(id => id !== modelId)
        : [...prev, modelId];
      
      // Eğer checkbox kaldırıldıysa ve phoneModel bu model ise, phoneModel'i de temizle
      if (isSelected && phoneModel === modelId.toString()) {
        setPhoneModel('');
        // URL'den phone_model parametresini kaldır
        const newSearchParams = new URLSearchParams(searchParams);
        newSearchParams.delete('phone_model');
        navigate(`/products?${newSearchParams.toString()}`, { replace: true });
      }
      
      return newSelected;
    });
  };

  const handleSort = (type) => {
    setSortType(type);
  };

  const handlePageChange = (pageNumber) => {
    // Eğer geçerli bir sayfa numarası değilse işlem yapma
    if (pageNumber < 1 || pageNumber > pagination.totalPages || pageNumber === currentPage) {
      return;
    }
    
    // Önce loading state'i güncelle
    setLoading(true);
    
    // Sayfa numarasını güncelle
    setCurrentPage(pageNumber);
    
    // Sayfanın en üstüne kaydırma işlemini bir Promise olarak tanımla
    const scrollPromise = new Promise((resolve) => {
      // Sayfanın en üstüne kaydır
      scrollToPageTop();
      
      // Kaydırma işleminin tamamlanması için bir süre bekle
      setTimeout(resolve, 500);
    });
    
    // Kaydırma işlemi tamamlandıktan sonra veri yükleme işlemini başlat
    scrollPromise.then(() => {
      // Veri yükleme işlemini başlat
      const loadPageData = async () => {
        try {
          // Query parametrelerini oluştur
          const params = {};
          
          // Koleksiyon filtresi
          if (selectedCollections.length > 0) {
            params.collections = selectedCollections.join(',');
          }
          
          // Telefon modeli filtresi
          if (phoneModel) {
            params.phone_model = phoneModel;
          }
          
          // Fiyat filtresi
          if (priceRange[0] > 0) {
            params.min_price = priceRange[0];
          }
          if (priceRange[1] < 1000) {
            params.max_price = priceRange[1];
          }
          
          // Arama filtresi
          if (searchText) {
            params.search = searchText;
          }
          
          // Sıralama
          if (sortType !== 'default') {
            params.sort = sortType;
          }
          
          // Sayfalama
          params.page = pageNumber;
          params.limit = 12;
          params.appendResults = false; // Önceki sonuçları temizle, yeni sayfayı göster
          
          // Ürünleri getir
          await fetchProducts(params);
        } catch (error) {
          console.error('Error loading page:', error);
          toast.error('Sayfa yüklenirken bir hata oluştu.');
        } finally {
          setLoading(false);
        }
      };
      
      // Veri yükleme işlemini başlat
      loadPageData();
    });
  };

  const handleImageError = (productId) => {
    setImageErrors(prev => ({
      ...prev,
      [productId]: true
    }));
  };

  // Aktif filtreleri hesapla
  const getActiveFilters = () => {
    const filters = [];
    
    if (searchText) {
      filters.push(`Arama: ${searchText}`);
    }
    
    selectedCollections.forEach(collection => {
      filters.push(`Koleksiyon: ${collection}`);
    });
    
    // Telefon modeli filtresi - phoneModel varsa onu kullan, yoksa selectedPhoneModels'i kullan
    // Aynı modelin iki kez gösterilmesini önlemek için
    if (phoneModel) {
      const model = phoneModels.find(m => m.id === parseInt(phoneModel));
      if (model) {
        filters.push(`Model: ${model.brand} ${model.model_name}`);
      }
    } else {
      // phoneModel yoksa selectedPhoneModels'i kontrol et
      selectedPhoneModels.forEach(modelId => {
        const model = phoneModels.find(m => m.id === modelId);
        if (model) {
          filters.push(`Model: ${model.brand} ${model.model_name}`);
        }
      });
    }
    
    if (priceRange[0] > 0 || priceRange[1] < 1000) {
      filters.push(`Fiyat: ${priceRange[0]} TL - ${priceRange[1]} TL`);
    }
    
    return filters;
  };

  // Handle custom design card click
  const handleCustomDesignClick = (selectedModel, selectedColor) => {
    navigate('/custom-design', { 
      state: { 
        selectedModelId: selectedModel?.id || null, 
        selectedColorId: selectedColor?.id || null 
      }
    });
  };

  // Sidebar handler fonksiyonları
  const formatCartItemsForSidebar = () => {
    return cart.map(item => {
      // Görsel URL'sini daha güvenli şekilde al
      let imageUrl = null;
      if (item.product?.images) {
        // Eğer images bir array ise ilk elemanı al
        if (Array.isArray(item.product.images)) {
          imageUrl = item.product.images[0];
        } else {
          // Eğer images bir string ise direkt kullan
          imageUrl = item.product.images;
        }
      }
      
      // Model ve renk bilgisini birleştir
      const modelInfo = item.selectedModel ? `${item.selectedModel.brand} ${item.selectedModel.model_name}` : 'Model seçilmedi';
      const colorInfo = item.selectedColor ? ` - ${item.selectedColor.name}` : '';
      const variant = modelInfo + colorInfo;
      
      return {
        id: item.product_id || item.product?.id,
        name: item.product?.name || 'Ürün',
        variant: variant,
        price: item.product?.price || 0,
        quantity: item.quantity || 1,
        image: imageUrl,
        color: item.selectedColor
      };
    });
  };

  const handleUpdateQuantity = async (productId, variant, newQuantity) => {
    try {
      console.log('🔄 handleUpdateQuantity called:', { productId, variant, newQuantity });
      
      // Variant'tan model ve renk bilgisini çıkar
      const variantParts = variant.split(' - ');
      const modelInfo = variantParts[0];
      const colorInfo = variantParts[1];
      
      // Cart item'ı model ve renk bilgisine göre bul
      const cartItem = cart.find(item => {
        const itemModelInfo = item.selectedModel ? `${item.selectedModel.brand} ${item.selectedModel.model_name}` : '';
        const itemColorInfo = item.selectedColor ? item.selectedColor.name : '';
        const itemVariant = itemModelInfo + (itemColorInfo ? ` - ${itemColorInfo}` : '');
        
        return itemVariant === variant;
      });
      
      if (cartItem) {
        await updateQuantity(productId, cartItem.selectedModel?.id, cartItem.selectedColor?.id, newQuantity);
      } else {
        console.warn('Güncellenecek ürün sepet içinde bulunamadı:', { productId, variant });
      }
    } catch (error) {
      console.error('Miktar güncelleme hatası:', error);
      
      // Stok yetersizliği kontrolü
      if (error.response?.data?.error === 'Yetersiz stok' || 
          error.response?.data?.error === 'Bu model için toplam sepet limiti aşıldı') {
        toast.error(error.response.data.error);
      } else {
        toast.error('Miktar güncellenirken bir hata oluştu');
      }
    }
  };

  const handleRemoveItem = async (productId, variant) => {
    try {
      console.log('❌ handleRemoveItem called:', { productId, variant });
      
      // Variant'tan model ve renk bilgisini çıkar
      const variantParts = variant.split(' - ');
      const modelInfo = variantParts[0];
      const colorInfo = variantParts[1];
      
      // Cart item'ı model ve renk bilgisine göre bul
      const cartItem = cart.find(item => {
        const itemModelInfo = item.selectedModel ? `${item.selectedModel.brand} ${item.selectedModel.model_name}` : '';
        const itemColorInfo = item.selectedColor ? item.selectedColor.name : '';
        const itemVariant = itemModelInfo + (itemColorInfo ? ` - ${itemColorInfo}` : '');
        
        return itemVariant === variant;
      });
      
      if (cartItem) {
        // Tracking için product bilgisini al
        const productForTracking = cartItem.product || { id: productId, name: cartItem.product_name || 'Ürün' };
        trackProductRemoveFromCart(productForTracking);
        
        await removeFromCart(productId, cartItem.selectedModel?.id, cartItem.selectedColor?.id);
        toast.success('Ürün sepetten kaldırıldı');
      } else {
        console.warn('Kaldırılacak ürün sepet içinde bulunamadı:', { productId, variant });
      }
    } catch (error) {
      console.error('Ürün kaldırma hatası:', error);
      toast.error('Ürün kaldırılırken bir hata oluştu');
    }
  };

  const handleViewCart = () => {
    setIsSidebarOpen(false);
    navigate('/cart');
  };

  const handleCheckout = () => {
    setIsSidebarOpen(false);
    navigate('/checkout');
  };

  if (productsError || collectionsError) {
    return <div className="text-center mt-5 text-danger">
      <p>{productsError || collectionsError}</p>
    </div>;
  }

  return (
    <div className="products-page">
      {/* SEO için yapılandırılmış veri */}
      <Helmet>
        <title>Ürünler | Mr. Brown</title>
        <meta name="description" content="Mr. Brown telefon kılıfları, aksesuarlar ve daha fazlası. Yüzlerce farklı tasarım ve model seçeneği ile kendi tarzınızı yansıtın." />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "ItemList",
            "itemListElement": products.slice(0, Math.min(products.length, 10)).map((product, index) => ({
              "@type": "ListItem",
              "position": index + 1,
              "item": {
                "@type": "Product",
                "name": product.name,
                "image": product.image_url,
                "description": product.description,
                "offers": {
                  "@type": "Offer",
                  "price": product.price,
                  "priceCurrency": "TRY",
                  "availability": "https://schema.org/InStock"
                }
              }
            }))
          })}
        </script>
      </Helmet>

      {/* Paralaks Arka Plan */}
      <div className="absolute inset-0 z-0">
        {/* Arka plan için beyaz overlay */}
        <div className="absolute inset-0 bg-white opacity-90 z-0"></div>
        {/* Gradient overlay */}
        <div className="absolute inset-0 bg-gradient-to-b from-amber-50/80 to-white/90 z-1"></div>
      </div>
      
      {/* Dekoratif Şekiller */}
      <div className="absolute top-20 left-10 w-32 h-32 rounded-full bg-amber-300 opacity-20 animate-blob"></div>
      <div className="absolute bottom-20 right-10 w-40 h-40 rounded-full bg-amber-500 opacity-10 animate-blob animation-delay-2000"></div>
      <div className="absolute top-1/3 right-1/4 w-24 h-24 rounded-full bg-amber-700 opacity-10 animate-blob animation-delay-4000"></div>

      {/* Mobile view */}
      <MobileProductsView
        collections={collections.map(c => c.name)}
        selectedCollections={selectedCollections}
        setSelectedCollections={setSelectedCollections}
        priceRange={priceRange}
        setPriceRange={setPriceRange}
        phoneModel={phoneModel}
        setPhoneModel={setPhoneModel}
        phoneModels={phoneModels}
        selectedPhoneModels={selectedPhoneModels}
        setSelectedPhoneModels={setSelectedPhoneModels}
        handleCollectionChange={handleCollectionChange}
        handlePriceChange={handlePriceChange}
        handlePhoneModelChange={handlePhoneModelChange}
        handlePhoneModelCheckboxChange={handlePhoneModelCheckboxChange}
        handleSort={handleSort}
        displayLimit={12}
        totalProducts={products.length}
        onLoadMore={handlePageChange}
        searchText={searchText}
        setSearchText={setSearchText}
        activeFilters={getActiveFilters()}
        sortType={sortType}
        setSortType={setSortType}
        pagination={pagination}
        loading={loading}
        currentPage={currentPage}
        searchParams={searchParams}
        navigate={navigate}
        mobileIsSticky={mobileIsSticky}
        mobileHeaderRef={mobileHeaderRef}
      />

      <Container fluid="lg" className="mt-0">
        <Row className="products-main-row" ref={productsMainRowRef}>
          {/* Desktop Filters */}
          <Col lg={3} className="d-none d-lg-block filters-column" ref={filtersColumnRef}>
            <div 
              className={`filters-wrapper sticky-top ${isSticky ? 'sticky-active' : ''}`}
              ref={filtersWrapperRef}
              style={isSticky && filtersWrapperWidth ? {
                position: 'fixed',
                top: '96px',
                width: `${filtersWrapperWidth}px`,
                zIndex: 10,
                willChange: 'transform',
                transform: 'translateZ(0)'
              } : {}}
            >
              <div className="filter-header">
                <div className="d-flex justify-content-between align-items-center">
                  <h4>Filtreler</h4>
                  <div className="results-count">
                    {products.length} ürün bulundu
                  </div>
                </div>
                {getActiveFilters().length > 0 && (
                  <div className="d-flex justify-content-between align-items-center mt-2">
                    <Button 
                      variant="link" 
                      className="p-0 text-danger"
                      onClick={() => {
                        setSearchText('');
                        setSelectedCollections([]);
                        setPhoneModel('');
                        setSelectedPhoneModels([]);
                        setPriceRange([0, 1000]);
                      }}
                    >
                      Tümünü Temizle
                    </Button>
                  </div>
                )}
                {getActiveFilters().length > 0 && (
                  <div className="active-filters mt-3">
                    <h6 className="mb-2 text-muted">Aktif Filtreler</h6>
                    <div className="filter-badges">
                      {getActiveFilters().map((filter, index) => {
                        const handleFilterRemove = (e) => {
                          e.stopPropagation();
                          // İlgili filtreyi kaldır
                          if (filter.includes('Koleksiyon:')) {
                            handleCollectionChange(filter.split(': ')[1]);
                          } else if (filter.includes('Model:')) {
                            const modelName = filter.split(': ')[1];
                            const model = phoneModels.find(m => `${m.brand} ${m.model_name}` === modelName);
                            if (model) {
                              // Hem phoneModel hem de selectedPhoneModels'den kaldır
                              if (phoneModel === model.id.toString()) {
                                setPhoneModel('');
                              }
                              // selectedPhoneModels'den kaldır
                              setSelectedPhoneModels(prev => prev.filter(id => id !== model.id));
                              // URL'den phone_model parametresini kaldır
                              const newSearchParams = new URLSearchParams(searchParams);
                              newSearchParams.delete('phone_model');
                              navigate(`/products?${newSearchParams.toString()}`, { replace: true });
                            }
                          } else if (filter.includes('Arama:')) {
                            // Arama filtresini kaldır
                            setSearchText('');
                            // URL'den search parametresini kaldır
                            const newSearchParams = new URLSearchParams(searchParams);
                            newSearchParams.delete('search');
                            navigate(`/products?${newSearchParams.toString()}`, { replace: true });
                          } else if (filter.includes('Fiyat:')) {
                            // Fiyat aralığı filtresini varsayılan değerlere sıfırla
                            setPriceRange([0, 1000]);
                            // URL'den fiyat parametrelerini kaldır
                            const newSearchParams = new URLSearchParams(searchParams);
                            newSearchParams.delete('min_price');
                            newSearchParams.delete('max_price');
                            navigate(`/products?${newSearchParams.toString()}`, { replace: true });
                          }
                        };

                        return (
                          <Badge 
                            key={index} 
                            bg="light" 
                            text="dark" 
                            className="me-2 mb-2 filter-badge d-inline-flex align-items-center"
                            style={{ cursor: 'pointer' }}
                            onClick={handleFilterRemove}
                          >
                            <span>{filter}</span>
                            <BsX 
                              className="ms-1" 
                              style={{ cursor: 'pointer' }}
                              onClick={handleFilterRemove}
                            />
                          </Badge>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>

              <div className="filter-section">
                <h6 className="filter-title">
                  <BsSortDown className="me-2" />
                  Sıralama
                </h6>
                <Form.Select 
                  value={sortType}
                  onChange={(e) => setSortType(e.target.value)}
                  className="custom-select"
                >
                  <option value="default">Öne Çıkanlar Önce</option>
                  <option value="price-asc">Fiyat: Düşükten Yükseğe</option>
                  <option value="price-desc">Fiyat: Yüksekten Düşüğe</option>
                  <option value="newest">En Yeniler</option>
                </Form.Select>
              </div>

              <div className="filter-section">
                <h6 className="filter-title">
                  <BsSearch className="me-2" />
                  Ürün Ara
                </h6>
                <div className="search-wrapper">
                  <BsSearch className="search-icon" />
                  <Form.Control
                    type="text"
                    className="search-input"
                    placeholder="Ürün adı"
                    value={searchText}
                    onChange={(e) => setSearchText(e.target.value)}
                  />
                </div>
              </div>

              <div className="filter-section">
                <div 
                  className="filter-title-clickable"
                  onClick={() => setShowDesktopCollections(!showDesktopCollections)}
                  style={{ cursor: 'pointer' }}
                >
                  <h6 className="filter-title d-flex justify-content-between align-items-center">
                    <span>
                      <BsCollection className="me-2" />
                      Koleksiyonlar
                    </span>
                    {showDesktopCollections ? (
                      <BsChevronDown />
                    ) : (
                      <BsChevronRight />
                    )}
                  </h6>
                </div>
                <Collapse in={showDesktopCollections}>
                  <div>
                    {collections.map((collection) => (
                      <Form.Check
                        key={collection.id}
                        type="checkbox"
                        id={`collection-${collection.id}`}
                        checked={selectedCollections.includes(collection.name)}
                        onChange={() => handleCollectionChange(collection.name)}
                        label={collection.name}
                        className="collection-checkbox"
                      />
                    ))}
                  </div>
                </Collapse>
              </div>

              <div className="filter-section">
                <div 
                  className="filter-title-clickable"
                  onClick={() => setShowDesktopPhoneModels(!showDesktopPhoneModels)}
                  style={{ cursor: 'pointer' }}
                >
                  <h6 className="filter-title d-flex justify-content-between align-items-center">
                    <span>
                      <BsPhone className="me-2" />
                      Telefon Modeli
                    </span>
                    {showDesktopPhoneModels ? (
                      <BsChevronDown />
                    ) : (
                      <BsChevronRight />
                    )}
                  </h6>
                </div>
                <Collapse in={showDesktopPhoneModels}>
                  <div>
                    {phoneModels.filter(model => model.is_active).map(model => (
                      <Form.Check
                        key={model.id}
                        type="checkbox"
                        id={`phone-model-${model.id}`}
                        label={`${model.brand} ${model.model_name}`}
                        checked={selectedPhoneModels.includes(model.id)}
                        onChange={() => handlePhoneModelCheckboxChange(model.id)}
                        className="collection-checkbox"
                      />
                    ))}
                  </div>
                </Collapse>
              </div>

              <div className="filter-section">
                <h6 className="filter-title">
                  <BsCurrencyDollar className="me-2" />
                  Fiyat Aralığı
                </h6>
                <Row className="g-2">
                  <Col>
                    <Form.Control
                      type="number"
                      value={priceRange[0]}
                      onChange={(e) => handlePriceChange([parseInt(e.target.value), priceRange[1]])}
                      min="0"
                      placeholder="Min"
                      className="custom-input"
                    />
                  </Col>
                  <Col xs="auto" className="d-flex align-items-center">-</Col>
                  <Col>
                    <Form.Control
                      type="number"
                      value={priceRange[1]}
                      onChange={(e) => handlePriceChange([priceRange[0], parseInt(e.target.value)])}
                      min="0"
                      placeholder="Max"
                      className="custom-input"
                    />
                  </Col>
                </Row>
              </div>
            </div>
          </Col>

          {/* Products Grid */}
          <Col lg={9} className="products-column">
            {loading ? (
              <div className="text-center mt-5">
                <div className="spinner-border text-primary" role="status">
                  <span className="visually-hidden">Yükleniyor...</span>
                </div>
                <p className="mt-2">Ürünler yükleniyor...</p>
              </div>
            ) : (
              <>
                <div className={`row g-3 products-grid ${mobileIsSticky ? 'mobile-sticky-gap' : ''}`}>
                                     {/* Kendin Tasarla Kartı - ilk pozisyonda göster */}
                   <div className="col-6 col-md-4 col-lg-4 product-col">
                     <CustomDesignCard 
                       phoneModels={phoneModels}
                       onCustomDesignClick={handleCustomDesignClick}
                     />
                   </div>
                  
                  {/* Mevcut ürünler */}
                  {products.map((product) => (
                    <div key={product.id} className="col-6 col-md-4 col-lg-4 product-col">
                      <ProductCard 
                        product={product} 
                        onImageError={() => handleImageError(product.id)}
                        useDefaultImage={imageErrors[product.id]}
                        defaultImage={defaultImage}
                        onAddToCartSuccess={() => setIsSidebarOpen(true)}
                        defaultSelectedModel={phoneModel || null}
                      />
                    </div>
                  ))}
                </div>

                {/* Sayfalama */}
                {products.length > 0 && pagination.totalPages > 1 && (
                  <div className="pagination-container text-center mt-4 mb-5">
                    <Pagination className="justify-content-center">
                      <Pagination.First 
                        onClick={() => handlePageChange(1)} 
                        disabled={currentPage === 1 || loading}
                      />
                      <Pagination.Prev 
                        onClick={() => handlePageChange(currentPage - 1)} 
                        disabled={currentPage === 1 || loading}
                      />
                      
                      {/* Sayfa numaralarını göster */}
                      {Array.from({ length: pagination.totalPages }, (_, i) => i + 1)
                        .filter(page => {
                          // Mevcut sayfanın etrafındaki 2 sayfayı, ilk ve son sayfayı göster
                          return (
                            page === 1 || 
                            page === pagination.totalPages || 
                            Math.abs(page - currentPage) <= 2
                          );
                        })
                        .map((page, index, array) => {
                          // Eğer sayfa numaraları arasında boşluk varsa ellipsis ekle
                          if (index > 0 && array[index - 1] !== page - 1) {
                            return (
                              <React.Fragment key={`ellipsis-${page}`}>
                                <Pagination.Ellipsis disabled />
                                <Pagination.Item
                                  key={page}
                                  active={page === currentPage}
                                  onClick={() => handlePageChange(page)}
                                  disabled={loading}
                                >
                                  {page}
                                </Pagination.Item>
                              </React.Fragment>
                            );
                          }
                          
                          return (
                            <Pagination.Item
                              key={page}
                              active={page === currentPage}
                              onClick={() => handlePageChange(page)}
                              disabled={loading}
                            >
                              {page}
                            </Pagination.Item>
                          );
                        })}
                      
                      <Pagination.Next 
                        onClick={() => handlePageChange(currentPage + 1)} 
                        disabled={currentPage === pagination.totalPages || loading}
                      />
                      <Pagination.Last 
                        onClick={() => handlePageChange(pagination.totalPages)} 
                        disabled={currentPage === pagination.totalPages || loading}
                      />
                    </Pagination>
                    
                    {loading && (
                      <div className="text-center mt-3">
                        <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                        Sayfa yükleniyor...
                      </div>
                    )}
                  </div>
                )}
              </>
            )}
          </Col>
        </Row>
      </Container>

      {/* Özel CSS Animasyonları için stil */}
      <style jsx>{`
        .animate-blob {
          animation: blob 7s infinite;
        }
        
        .animation-delay-2000 {
          animation-delay: 2s;
        }
        
        .animation-delay-4000 {
          animation-delay: 4s;
        }
        
        @keyframes blob {
          0% {
            transform: translate(0px, 0px) scale(1);
          }
          33% {
            transform: translate(30px, -50px) scale(1.1);
          }
          66% {
            transform: translate(-20px, 20px) scale(0.9);
          }
          100% {
            transform: translate(0px, 0px) scale(1);
          }
        }
      `}</style>

      {/* Shopping Cart Sidebar */}
      <ShoppingCartSidebar
        isOpen={isSidebarOpen}
        onClose={() => setIsSidebarOpen(false)}
        cartItems={formatCartItemsForSidebar()}
        onUpdateQuantity={handleUpdateQuantity}
        onRemoveItem={handleRemoveItem}
        onViewCart={handleViewCart}
        onCheckout={handleCheckout}
      />

      {/* Scroll to Top Button - Sadece Mobilde */}
      {showScrollTop && (
        <button
          className="scroll-to-top-btn d-lg-none"
          onClick={scrollToTop}
          aria-label="Yukarı çık"
        >
          <BsArrowUp />
        </button>
      )}
    </div>
  );
}

export default Products;